import React from "react";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import MaterialIcon from "@/components/ui/MaterialIcon";
import { useIngestionStatus, type IngestionStatus } from "../hooks/useIngestionStatus";

interface IngestionStatusBannerProps {
  onStatusChange?: (status: IngestionStatus) => void;
}

const IngestionStatusBanner: React.FC<IngestionStatusBannerProps> = ({
  onStatusChange,
}) => {
  const { status, errorMessage } = useIngestionStatus();

  React.useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  if (status === "not_started" || status === "completed") {
    return null;
  }

  if (status === "failed") {
    return (
      <Card className="border-destructive bg-destructive/10 px-6 py-8">
        <div className="flex flex-col items-center text-center gap-3">
          <MaterialIcon icon="error" size={48} className="text-destructive" weight={400} />
          <div className="space-y-1">
            <Text className="text-destructive">
              There was a problem importing your data.
            </Text>
            <Text variant="sm" className="font-medium text-destructive">
              Data import failed
            </Text>
            {errorMessage && (
              <Text variant="sm" className="text-destructive/80">
                Reason: {errorMessage}
              </Text>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30 bg-primary/5 px-4 py-3">
      <Text variant="sm" className="font-medium text-primary">
        Importing historical data from Intercom...
      </Text>
      <Text variant="sm" className="text-muted-foreground mt-1">
        This usually takes a few minutes.
      </Text>
      <div className="mt-3 h-1.5 w-full rounded-full bg-primary/20 overflow-hidden">
        <div
          className="h-full w-1/3 rounded-full bg-primary animate-[indeterminate_1.5s_ease-in-out_infinite]"
          style={{
            animation: "indeterminate 1.5s ease-in-out infinite",
          }}
        />
      </div>
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </Card>
  );
};

export default IngestionStatusBanner;
